// Pressure.js
import React, { useState, useCallback } from "react";
import MainCalculatorsList from "./MainCalculators";
import SharedHelmet from "../Helmet";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
const TITLE = "Kalkulator Tekanan - Hitung Tekanan, Gaya, atau Luas";
const DESCRIPTION =
  "Kalkulator tekanan komprehensif: Hitung tekanan, gaya, atau luas menggunakan nilai input. Alat yang akurat dan mudah digunakan.";
const KEYWORDS = "tekanan, menghitung tekanan, menghitung gaya, menghitung luas, kalkulator tekanan";
const OG_URL = "https://do-calculate.com/calculator/id/pressure";
function PressureCalculator() {
  const [pressure, setPressure] = useState("");
  const [force, setForce] = useState("");
  const [area, setArea] = useState("");
  const [calculationMode, setCalculationMode] = useState("calculatePressure");
  const [error, setError] = useState("");
  const [showSteps, setShowSteps] = useState(false);
  const updateCalculations = useCallback(() => {
    setError("");
    const pressureValue = parseFloat(pressure) || 0;
    const forceValue = parseFloat(force) || 0;
    const areaValue = parseFloat(area) || 0;
    switch (calculationMode) {
      case "calculatePressure":
        if (forceValue > 0 && areaValue > 0) {
          const calculatedPressure = forceValue / areaValue;
          setPressure(Math.round(calculatedPressure * 100) / 100);
        } else {
          setError("Harap masukkan gaya dan luas dengan benar");
        }
        break;
      case "calculateForce":
        if (pressureValue > 0 && areaValue > 0) {
          const calculatedForce = pressureValue * areaValue;
          setForce(Math.round(calculatedForce * 100) / 100);
        } else {
          setError("Harap masukkan tekanan dan luas dengan benar");
        }
        break;
      case "calculateArea":
        if (pressureValue > 0 && forceValue > 0) {
          const calculatedArea = forceValue / pressureValue;
          setArea(Math.round(calculatedArea * 100) / 100);
        } else {
          setError("Harap masukkan tekanan dan gaya dengan benar");
        }
        break;
      default:
        break;
    }
  }, [pressure, force, area, calculationMode]);
  const handleCalculateClick = () => {
    updateCalculations();
    setShowSteps(true);
  };
  const handlePressureChange = (e) => setPressure(e.target.value);
  const handleForceChange = (e) => setForce(e.target.value);
  const handleAreaChange = (e) => setArea(e.target.value);
  const handleModeChange = (e) => setCalculationMode(e.target.value);
  const getSolutionSteps = () => {
    switch (calculationMode) {
      case "calculatePressure":
        return (
          <div className="alert alert-info">
            <h4>Langkah-langkah Menghitung Tekanan:</h4>
            <ol>
              <li>Masukkan gaya (dalam Newton) pada kolom yang disediakan.</li>
              <li>Masukkan luas (dalam meter persegi) pada kolom yang disediakan.</li>
              <li>Tekan tombol "Hitung" untuk mendapatkan tekanan.</li>
              <li>Tekanan sama dengan gaya / luas.</li>
            </ol>
          </div>
        );
      case "calculateForce":
        return (
          <div className="alert alert-info">
            <h4>Langkah-langkah Menghitung Gaya:</h4>
            <ol>
              <li>Masukkan tekanan (dalam Pascal) pada kolom yang disediakan.</li>
              <li>Masukkan luas (dalam meter persegi) pada kolom yang disediakan.</li>
              <li>Tekan tombol "Hitung" untuk mendapatkan gaya.</li>
              <li>Gaya sama dengan tekanan × luas.</li>
            </ol>
          </div>
        );
      case "calculateArea":
        return (
          <div className="alert alert-info">
            <h4>Langkah-langkah Menghitung Luas:</h4>
            <ol>
              <li>Masukkan tekanan (dalam Pascal) pada kolom yang disediakan.</li>
              <li>Masukkan gaya (dalam Newton) pada kolom yang disediakan.</li>
              <li>Tekan tombol "Hitung" untuk mendapatkan luas.</li>
              <li>Luas sama dengan gaya / tekanan.</li>
            </ol>
          </div>
        );
      default:
        return null;
    }
  };
  return (
    <div>
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ height: "50px" }}
          >
            <h1 style={{ fontSize: "17px" }}>{TITLE}</h1>
          </div>
          <div className="border border-light">
            <div className="row">
              <div>
                <p>
                  Kalkulator tekanan ini memungkinkan Anda melakukan tiga jenis perhitungan
                  berdasarkan nilai input. Pilih jenis perhitungan yang sesuai dan masukkan
                  nilai yang tersedia untuk menghitung nilai yang hilang dengan akurat dan mudah.
                </p>
                <h2 style={{ fontSize: "17px" }}>
                  Menemukan Tekanan dengan Gaya dan Luas yang Diketahui
                </h2>
                <p>
                  Jika Anda mengetahui gaya (dalam Newton) dan luas (dalam meter persegi), Anda dapat
                  menghitung tekanan (dalam Pascal) menggunakan rumus berikut:
                </p>
                <p>
                  <b>Tekanan = Gaya / Luas</b>
                </p>
                <h2 style={{ fontSize: "17px" }}>
                  Menemukan Gaya dengan Tekanan dan Luas yang Diketahui
                </h2>
                <p>
                  Jika Anda mengetahui tekanan (dalam Pascal) dan luas (dalam meter persegi), Anda dapat
                  menghitung gaya (dalam Newton) menggunakan rumus berikut:
                </p>
                <p>
                  <b>Gaya = Tekanan × Luas</b>
                </p>
                <h2 style={{ fontSize: "17px" }}>
                  Menemukan Luas dengan Tekanan dan Gaya yang Diketahui
                </h2>
                <p>
                  Jika Anda mengetahui tekanan (dalam Pascal) dan gaya (dalam Newton), Anda dapat
                  menghitung luas (dalam meter persegi) menggunakan rumus berikut:
                </p>
                <p>
                  <b>Luas = Gaya / Tekanan</b>
                </p>
                <p>
                  Gunakan alat ini untuk memasukkan nilai yang tersedia dan menghitung nilai yang hilang
                  dengan akurat, memudahkan Anda melakukan perhitungan terkait tekanan.
                </p>
                <hr />
              </div>
            </div>
            <MobileUnderCalAds />
            <div className="row">
              <div className="col-sm-12 col-md-7 calculatorStyle">
                <div className="p-3 mb-2 bg-success text-white">
                  <h2 style={{ fontSize: "16px" }}>{TITLE}</h2>
                </div>
                <hr />
                <div >
                  <div className="form-group align-items-center">
                    <b>
                      <label
                        className="form-label ms-2"
                        htmlFor="calculationMode"
                      >
                        Pilih Jenis Perhitungan
                      </label>
                    </b>
                    <div className="d-flex flex-row mb-2">
                      <div>
                        <input
                          type="radio"
                          id="calculatePressure"
                          name="calculationMode"
                          value="calculatePressure"
                          checked={calculationMode === "calculatePressure"}
                          onChange={handleModeChange}
                        />
                        <label htmlFor="calculatePressure" className="ms-2">
                          Hitung Tekanan
                        </label>
                      </div>
                      <div className="ms-3">
                        <input
                          type="radio"
                          id="calculateForce"
                          name="calculationMode"
                          value="calculateForce"
                          checked={calculationMode === "calculateForce"}
                          onChange={handleModeChange}
                        />
                        <label htmlFor="calculateForce" className="ms-2">
                          Hitung Gaya
                        </label>
                      </div>
                      <div className="ms-3">
                        <input
                          type="radio"
                          id="calculateArea"
                          name="calculationMode"
                          value="calculateArea"
                          checked={calculationMode === "calculateArea"}
                          onChange={handleModeChange}
                        />
                        <label htmlFor="calculateArea" className="ms-2">
                          Hitung Luas
                        </label>
                      </div>
                    </div>
                  </div>
                  <br />
                  {calculationMode !== "calculatePressure" && (
                    <div className="form-group d-flex align-items-center">
                      <b>
                        <label className="form-label ms-2" htmlFor="forceInput">
                          Gaya (Newton)
                        </label>
                      </b>
                      <input
                        id="forceInput"
                        type="number"
                        placeholder="1000"
                        value={force}
                        onChange={handleForceChange}
                        className="form-control"
                        style={{ width: "90%" }}
                      />
                    </div>
                  )}
                  {calculationMode !== "calculateForce" && (
                    <div className="form-group d-flex align-items-center">
                      <b>
                        <label className="form-label ms-2" htmlFor="areaInput">
                          Luas (meter persegi)
                        </label>
                      </b>
                      <input
                        id="areaInput"
                        type="number"
                        placeholder="10"
                        value={area}
                        onChange={handleAreaChange}
                        className="form-control"
                        style={{ width: "90%" }}
                      />
                    </div>
                  )}
                  {calculationMode !== "calculateArea" && (
                    <div className="form-group d-flex align-items-center">
                      <b>
                        <label
                          className="form-label ms-2"
                          htmlFor="pressureInput"
                        >
                          Tekanan (Pascal)
                        </label>
                      </b>
                      <input
                        id="pressureInput"
                        type="number"
                        placeholder="101325"
                        value={pressure}
                        onChange={handlePressureChange}
                        className="form-control"
                        style={{ width: "90%" }}
                      />
                    </div>
                  )}
                  <br />
                  <button
                    className="btn btn-success"
                    style={{ fontSize: "20px" }}
                    onClick={handleCalculateClick}
                  >
                    Hitung
                  </button>
                  <br/>                  <div>
                    { (
                      <>
                        {error !== "" && (
                          <p className="p-3 mb-2 bg-warning text-white">
                            Kesalahan: {error}
                          </p>
                        )}
                        {calculationMode === "calculatePressure" &&
                          pressure !== "" && (
                            <p className="p-3 mb-2 bg-success text-white">
                              Tekanan (Pascal): {pressure} Pascal
                            </p>
                          )}
                        {calculationMode === "calculateForce" &&
                          force !== "" && (
                            <p className="p-3 mb-2 bg-success text-white">
                              Gaya (Newton): {force} Newton
                            </p>
                          )}
                        {calculationMode === "calculateArea" && area !== "" && (
                          <p className="p-3 mb-2 bg-success text-white">
                            Luas (meter persegi): {area} meter persegi
                          </p>
                        )}
                        {showSteps && getSolutionSteps()}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <hr />
            <div className="row">
              <RelatedLinks />
              <hr />
            </div>
          </div>
        </div>
        <div className="col">
          <SecondAds />
          <br />
          <MainCalculatorsList></MainCalculatorsList>
          <br />
        </div>
         </div>
</div>
  );
}
export default PressureCalculator;